import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  BookOpen, History, BarChart3, BrainCircuit, Sparkles, Star,
  ArrowRight, Search, Zap, Layers, MessageSquare
} from 'lucide-react';

const HomeContainer = styled.div`
  min-height: 100vh;
  background: #141414;
  color: #e5e5e5;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 20px 12px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 60px;
  padding: 40px 20px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
    padding: 30px 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 16px;
  background: none;
  -webkit-background-clip: border-box;
  -webkit-text-fill-color: initial;
  color: #ffffff;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2.0rem; /* Slightly reduced to prevent overflow */
    line-height: 1.3;
    word-break: keep-all; /* Critical for Korean text */
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #a3a3a3;
  margin-bottom: 40px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
    word-break: keep-all; /* Prevent awkward breaks in Korean */
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Stack vertically */
    gap: 16px; /* Tighter gap */
  }
`;

const Card = styled(Link)`
  background: #141414;
  border: 1px solid #333;
  border-radius: 16px;
  padding: 30px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    background: #262626;
    border-color: #555;
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 768px) {
    padding: 20px; /* Reduced padding */
    flex-direction: row; /* Horizontal layout for mobile cards */
    align-items: center;
    gap: 16px;
  }
`;

const CardIcon = styled.div`
  background: ${props => props.$bg || '#333'};
  color: ${props => props.$color || '#fff'};
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  /* Prevent shrinking in flex row */
  flex-shrink: 0; 

  @media (max-width: 768px) {
    margin-bottom: 0; /* Remove bottom margin for row layout */
    width: 44px;
    height: 44px;
    
    & > svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #fff;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 4px;
  }
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  color: #aaa;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* Limit text lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const SectionHeader = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 24px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 16px;
  }
`;

function Home() {
  return (
    <HomeContainer>
      <ContentWrapper>
        <HeroSection>
          <HeroTitle>영어,<br />누르면 내 것이 됩니다.</HeroTitle>
          <HeroSubtitle>
            읽다가 막히는 단어가 있나요? 고민하지 말고 그저 <strong>'꾹'</strong> 누르세요.<br />
            그 순간, AI가 당신만의 선생님이 되어 모든 것을 알려줍니다.<br />
            <strong>NUREUM</strong> — 가장 직관적인 영어 학습의 시작.
          </HeroSubtitle>
        </HeroSection>

        <SectionHeader>
          <Zap size={28} color="#e50914" />
          Explore & Connect
        </SectionHeader>
        <MainGrid>
          <Card to="/feed">
            <CardIcon $bg="rgba(168, 85, 247, 0.2)" $color="#a855f7">
              <Layers size={28} />
            </CardIcon>
            <div>
              <CardTitle>세상을 누르다 (Feed)</CardTitle>
              <CardDesc>
                매일 업데이트되는 무한한 이야기들.<br />
                스크롤을 내리고, 세상을 누르며 탐험하세요.
              </CardDesc>
            </div>
          </Card>

          <Card to="/mystory">
            <CardIcon $bg="rgba(236, 72, 153, 0.2)" $color="#ec4899">
              <BookOpen size={28} />
            </CardIcon>
            <div>
              <CardTitle>나의 이야기 (MyStory)</CardTitle>
              <CardDesc>
                원하는 주제로 만드는 나만의 영어 동화.<br />
                AI와 함께 세상에 하나뿐인 이야기를 만들어보세요.
              </CardDesc>
            </div>
          </Card>

          <Card to="/ai-study">
            <CardIcon $bg="rgba(229, 9, 20, 0.2)" $color="#e50914">
              <Sparkles size={28} />
            </CardIcon>
            <div>
              <CardTitle>관심사를 누르다 (AI Study)</CardTitle>
              <CardDesc>
                당신이 좋아하는 주제를 선택하고 누르세요.<br />
                AI가 당신만을 위한 흥미진진한 지문을 만들어냅니다.
              </CardDesc>
            </div>
          </Card>

          <Card to="/community">
            <CardIcon $bg="rgba(16, 185, 129, 0.2)" $color="#10b981">
              <MessageSquare size={28} />
            </CardIcon>
            <div>
              <CardTitle>함께 누르다 (Community)</CardTitle>
              <CardDesc>
                다른 학습자들과 이야기를 나누세요.<br />
                함께 공부하며 서로의 성장을 응원합니다.
              </CardDesc>
            </div>
          </Card>
        </MainGrid>

        <SectionHeader>
          <BrainCircuit size={28} color="#e50914" />
          Review & Grow
        </SectionHeader>
        <MainGrid>
          <Card to="/sentence">
            <CardIcon $bg="rgba(59, 130, 246, 0.2)" $color="#3b82f6">
              <Search size={28} />
            </CardIcon>
            <div>
              <CardTitle>궁금함을 누르다 (Analysis)</CardTitle>
              <CardDesc>
                어렵고 복잡한 문장도 문제 없습니다.<br />
                누르는 순간, 구조와 의미가 투명하게 보입니다.
              </CardDesc>
            </div>
          </Card>

          <Card to="/quiz">
            <CardIcon $bg="#333" $color="#fff">
              <BrainCircuit size={28} />
            </CardIcon>
            <div>
              <CardTitle>기억을 누르다 (Quiz)</CardTitle>
              <CardDesc>
                손끝으로 익힌 단어를 퀴즈로 확인하세요.<br />
                반복해서 누르다 보면 어느새 장기 기억이 됩니다.
              </CardDesc>
            </div>
          </Card>

          <Card to="/quiz?mode=review">
            <CardIcon $bg="rgba(234, 179, 8, 0.2)" $color="#eab308">
              <History size={28} />
            </CardIcon>
            <div>
              <CardTitle>실수를 누르다 (Review)</CardTitle>
              <CardDesc>
                틀렸던 기억조차 소중한 자산입니다.<br />
                다시 한번 눌러서 완벽하게 내 것으로 만드세요.
              </CardDesc>
            </div>
          </Card>

          <Card to="/word-history">
            <CardIcon $bg="#333" $color="#fff">
              <BookOpen size={28} />
            </CardIcon>
            <div>
              <CardTitle>단어 발자국 (Word History)</CardTitle>
              <CardDesc>
                내가 눌렀던 모든 단어들이 여기에 있습니다.<br />
                쌓여가는 단어들만큼 성장하는 실력을 확인하세요.
              </CardDesc>
            </div>
          </Card>

          <Card to="/sentence-history">
            <CardIcon $bg="#333" $color="#fff">
              <Layers size={28} />
            </CardIcon>
            <div>
              <CardTitle>문장 발자국 (Sentence History)</CardTitle>
              <CardDesc>
                분석했던 모든 문장을 다시 볼 수 있습니다.<br />
                놓쳤던 문맥과 표현을 다시 확인해보세요.
              </CardDesc>
            </div>
          </Card>

          <Card to="/dashboard">
            <CardIcon $bg="#333" $color="#fff">
              <BarChart3 size={28} />
            </CardIcon>
            <div>
              <CardTitle>성장의 기록 (Dashboard)</CardTitle>
              <CardDesc>
                데이터로 증명되는 나의 누름(Learning).<br />
                꾸준한 학습 습관을 한눈에 보세요.
              </CardDesc>
            </div>
          </Card>
        </MainGrid>
      </ContentWrapper>
    </HomeContainer>
  );
}

export default Home;
